import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDown, Recycle, Users, MessageCircle } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { waGeneralLink } from "../lib/api";
import { IMAGES } from "../lib/images";

const line = (delay) => ({
    initial: { y: "115%", rotate: 3 },
    animate: { y: "0%", rotate: 0 },
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay },
});

const fade = (delay) => ({
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay },
});

function MiniCard({ src, className, alt }) {
    return (
        <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            className={`absolute rounded-2xl border-2 border-brand-ink bg-white p-1.5 shadow-hard-sm ${className}`}
            style={{ transform: "translateZ(70px)" }}
        >
            <img src={src} alt={alt} className="w-full aspect-square object-cover rounded-xl" />
        </motion.div>
    );
}

export default function Hero() {
    const sectionRef = useRef(null);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 14 });
    const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 14 });

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const driftY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const driftR = useTransform(scrollYProgress, [0, 1], [0, 6]);

    const onMouseMove = (e) => {
        const r = sectionRef.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section id="beranda" ref={sectionRef} onMouseMove={onMouseMove} className="relative overflow-hidden pt-28 md:pt-40 pb-16 md:pb-24">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(720px 480px at 85% 10%, rgba(255,197,37,0.30), transparent 65%), radial-gradient(600px 420px at 8% 85%, rgba(249,96,21,0.12), transparent 60%)" }} />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-10 items-center">
                <div>
                    <motion.div {...fade(0.05)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-brand-ink bg-white shadow-hard-sm">
                        <Recycle className="w-4 h-4 text-brand-forest" strokeWidth={2.6} />
                        <span className="text-xs font-extrabold uppercase tracking-[0.16em]">Marketplace Daur Ulang Kreatif</span>
                    </motion.div>

                    <h1 className="mt-7 font-display font-extrabold tracking-tight leading-[1.02] text-[13vw] sm:text-6xl lg:text-[4.6rem]">
                        <span className="block overflow-hidden pb-1">
                            <motion.span {...line(0.15)} className="block">Ubah Sampah</motion.span>
                        </span>
                        <span className="block overflow-hidden pb-1">
                            <motion.span {...line(0.28)} className="block"><span className="text-stroke">Kertas</span> Jadi</motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span {...line(0.41)} className="block">
                                <span className="relative inline-block">
                                    <span className="relative z-10 px-3 bg-brand-sunshine rounded-2xl -rotate-1 inline-block">Karya</span>
                                </span>{" "}
                                Bernilai.
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p {...fade(0.55)} className="mt-6 text-base sm:text-lg text-brand-ink/70 leading-relaxed max-w-xl">
                        Pasar kreator anak muda Indonesia — keranjang dari kertas gulung, tempat pensil cup bekas,
                        mainan cat dari bubur kertas. Semua lahir dari barang yang diselamatkan, dan semuanya bernilai ekonomi.
                    </motion.p>

                    <motion.div {...fade(0.68)} className="mt-8 flex flex-wrap items-center gap-3.5">
                        <button
                            data-testid="hero-explore-button"
                            onClick={() => scrollToId("katalog")}
                            className="inline-flex items-center gap-2 px-7 py-4 rounded-full border-2 border-brand-ink bg-brand-forest text-brand-cream font-display font-bold text-base shadow-hard hover:-translate-y-0.5 hover:shadow-hard-lg transition-all active:translate-y-0 active:shadow-hard"
                        >
                            Jelajahi Karya <ArrowDown className="w-5 h-5" strokeWidth={2.6} />
                        </button>
                        <a
                            data-testid="hero-cs-button"
                            href={waGeneralLink()}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-7 py-4 rounded-full border-2 border-brand-ink bg-white font-display font-bold text-base shadow-hard hover:-translate-y-0.5 hover:shadow-hard-lg transition-all active:translate-y-0 active:shadow-hard"
                        >
                            <MessageCircle className="w-5 h-5 text-brand-forest" strokeWidth={2.6} /> Chat Customer Service
                        </a>
                    </motion.div>

                    <motion.p {...fade(0.8)} className="mt-6 font-hand text-2xl text-brand-forest rotate-[-2deg]">
                        dari kertas bekas jadi glow-up!
                    </motion.p>
                </div>

                <motion.div className="relative flex justify-center lg:justify-end pb-10 lg:pb-0" style={{ perspective: 1200 }}>
                    <motion.div
                        style={{ y: driftY, rotate: driftR, transformStyle: "preserve-3d" }}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
                    >
                        <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative w-[270px] sm:w-[340px] lg:w-[400px]">
                            <div className="rounded-[28px] border-2 border-brand-ink overflow-hidden shadow-hard-lg rotate-2 bg-white">
                                <img src={IMAGES.paperPulpCat} alt="Mainan kucing dari bubur kertas daur ulang" className="w-full aspect-square object-cover" />
                            </div>
                            <div className="absolute -bottom-5 -left-5 px-4 py-2 rounded-full border-2 border-brand-ink bg-brand-sunshine font-display font-extrabold shadow-hard-sm rotate-[-6deg]">
                                Rp 25.000
                            </div>

                            <MiniCard src={IMAGES.paperPencilCup} alt="Tempat pensil dari kertas gulung bekas" className="-top-8 -left-10 sm:-left-16 w-24 sm:w-36 !rotate-[-8deg]" />
                            <MiniCard src={IMAGES.paperBasket} alt="Keranjang anyaman dari kertas majalah bekas" className="-bottom-10 -right-6 sm:-right-12 w-28 sm:w-40 !rotate-[7deg]" />

                            <motion.div
                                animate={{ y: [0, -12, 0] }}
                                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute top-10 -right-4 sm:-right-10 px-3.5 py-2 rounded-full border-2 border-brand-ink bg-brand-forestLight text-brand-forest text-xs font-extrabold shadow-hard-sm rotate-3"
                            >
                                100% Kertas Daur Ulang
                            </motion.div>
                            <motion.div
                                animate={{ y: [0, 10, 0] }}
                                transition={{ duration: 5.6, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                                className="absolute bottom-24 -left-4 sm:-left-14 px-3.5 py-2 rounded-full border-2 border-brand-ink bg-white text-xs font-extrabold shadow-hard-sm rotate-[-3deg]"
                            >
                                Karya Creator Lokal
                            </motion.div>
                            <motion.div
                                animate={{ y: [0, -8, 0] }}
                                transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                                className="absolute -top-6 left-8 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border-2 border-brand-ink bg-brand-forestLight text-brand-forest text-xs font-extrabold shadow-hard-sm rotate-2"
                            >
                                <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.8} /> Order via WhatsApp
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>

            <motion.div {...fade(1)} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 lg:mt-16 hidden sm:flex items-center gap-8 text-sm font-bold text-brand-ink/60">
                <span className="inline-flex items-center gap-2"><Recycle className="w-4 h-4 text-brand-forest" /> 100% Bahan Kertas Bekas</span>
                <span className="w-1.5 h-1.5 rounded-full bg-brand-ink/30" />
                <span className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-brand-carrot" /> Kreator Anak Muda</span>
                <span className="w-1.5 h-1.5 rounded-full bg-brand-ink/30" />
                <span className="inline-flex items-center gap-2"><MessageCircle className="w-4 h-4 text-brand-wa" /> Konsultasi via WhatsApp</span>
            </motion.div>
        </section>
    );
}
